import { useEffect } from 'react';

import { useIntl } from 'react-intl';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';
import {
	AnchorPageRelayQuery,
	shapeAnchorContentfulResponse,
} from 'src/contentful/graphql/anchor-page';

import { anchorPageRelayQuery } from '@/__generated_contentful__/anchorPageRelayQuery.graphql';
import { CarouselProps } from '@/components/confluence/carousel/src/carousel';
import { useLogMeInD0to6 } from '@/components/confluence/hooks/use-log-me-in-d0to6.tsx';
import HeroSection from '@/templates/software/confluence/confluence-software-page/src/sections/log-me-in/hero';
import {
	AnchorContentsResponse,
	PaidLandingPersonaTypes,
} from '@/utils/anchor-queries/get-anchor-contents';
import { experiments } from '@/utils/experiment/manifest';
import { useExperimentExposure } from '@/utils/experiment/use-experiment-exposure';
import { FeatureExposed } from '@/utils/feature-exposed/use-feature-exposed';

import { ConfluenceSoftwarePageProps } from './confluence-software-page';
import { carouselHeroMessages } from './messages/carousel-hero.messages';
import { ConfluencePagePaidLpConflAnchor } from './paid-lp-confl-anchor';
import { isTargetedSubAnchor } from './subAnchors/subAnchor-util.tsx';

export interface ConfluenceSoftwarePageAnchorProgramMgmtRelayProps extends ConfluenceSoftwarePageProps {
	anchorContents?: AnchorContentsResponse | null;
	persona?: string;
	queryRef?: PreloadedQuery<anchorPageRelayQuery>;
}
export const ConfluenceSoftwarePageAnchorProgramMgmt = (
	pageProps: ConfluenceSoftwarePageAnchorProgramMgmtRelayProps,
) => {
	const intl = useIntl();
	const [exposureSubAnchorFired, fireSubAnchorExposure] = useExperimentExposure(
		experiments.cfind4782LandingPageSubAnchorV3,
	);
	useEffect(() => {
		if (isTargetedSubAnchor(pageProps.subAnchor) && !exposureSubAnchorFired) {
			fireSubAnchorExposure();
		}
	}, [exposureSubAnchorFired, fireSubAnchorExposure]);

	const data = usePreloadedQuery(
		AnchorPageRelayQuery,
		pageProps.queryRef || (null as unknown as PreloadedQuery<anchorPageRelayQuery>),
	);
	pageProps = Object.assign({}, pageProps, {
		anchorContents: shapeAnchorContentfulResponse(data),
	});

	const { userProfileName, recentSites, shouldShowLogMeIn } = useLogMeInD0to6({
		availableProducts: pageProps.availableProducts,
		userProfile: pageProps.userProfile,
	});

	// TODO: add missing images
	const carouselSlides = [
		{
			primaryText: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.ProgramMgmt.project-goals.title',
				defaultMessage: 'Achieve project goals together',
			}),
			description: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.ProgramMgmt.project-goals.body',
				defaultMessage:
					'Brainstorm ideas, plan projects, take notes, and share knowledge—all in one place.',
			}),
			imgSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/1e81unagi717g13fj44qtgpk0h8r0537.webp',
			imgAltText: 'A Confluence page being edited',
		},
		{
			primaryText: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.ProgramMgmt.team-efficiency.title',
				defaultMessage: 'Streamline processes and team efficiency',
			}),
			description: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.ProgramMgmt.team-efficiency.body',
				defaultMessage:
					'Boost productivity by using Automation to coordinate workflows, prompting next steps when a dependency is completed.',
			}),
			imgSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/3rpsu66gd78285buk60k1t08w568ds37.webp',
			imgAltText: 'A Confluence automation rule',
		},
		{
			primaryText: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.ProgramMgmt.project-deadlines.title',
				defaultMessage: 'Hit project deadlines',
			}),
			description: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.ProgramMgmt.project-deadlines.body',
				defaultMessage:
					'Keep teams organized with dedicated workspaces, equipped with AI-powered search.',
			}),
			imgSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/fpd20037c363n21cfmh45nc5m50co161.webp',
			imgAltText: 'Organized workspace in Confluence',
		},
	];

	const carouselHeroProps: CarouselProps = {
		'aria-label': intl.formatMessage(carouselHeroMessages.ariaLabel),
		id: 'confluence-program-management-carousel',
		slides: carouselSlides,
	};

	const pageId = 'anchor-lp-conf-team-programmgmt';

	let customHeroSection = null;
	if (shouldShowLogMeIn) {
		customHeroSection = (
			<FeatureExposed experimentKey="cfind-2801-get-paid-users-back-to-product" group="Test">
				<HeroSection
					confluenceSites={recentSites}
					userProfileName={userProfileName}
					hideExploreMore
					loggedInPaid
				/>
			</FeatureExposed>
		);
	}

	return (
		<ConfluencePagePaidLpConflAnchor
			{...pageProps}
			pageId={pageId}
			persona={'unknown' as PaidLandingPersonaTypes}
			pageVariant="programMgmt"
			dataProductKey="confluence-AnchorLpConfProgramMgmt"
			carouselHeroProps={carouselHeroProps}
			keyword={pageProps.keyword}
			customHeroSection={customHeroSection}
		/>
	);
};
